import type { QueryClient } from '@tanstack/react-query'
import { isBrowser } from './env'
import { authClient } from './auth-client'
import type { AuthUser } from '@lunara/shared'

const CACHE_KEY = 'lunara_user'

interface BetterAuthUser {
  id: string
  email: string
  name: string
  image?: string | null
  role?: string
}

export function mapAuthUser(u: BetterAuthUser): AuthUser {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    image: u.image ?? null,
    role: (u.role === 'admin' ? 'admin' : 'user'),
  }
}

export function getCachedUser(): AuthUser | null {
  if (!isBrowser) return null
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

export function setCachedUser(user: AuthUser | null) {
  if (!isBrowser) return
  if (user) localStorage.setItem(CACHE_KEY, JSON.stringify(user))
  else localStorage.removeItem(CACHE_KEY)
}

export function clearCachedUser() {
  setCachedUser(null)
}

/** Called by the query/mutation caches on a 401 — sign out + bounce to /login. */
export async function handleSessionExpired(queryClient: QueryClient) {
  clearCachedUser()
  queryClient.setQueryData(['auth', 'session'], null)
  try {
    await authClient.signOut()
  } catch {
    // ignore — session may already be gone
  }
  if (isBrowser) {
    const from = window.location.pathname + window.location.search
    window.location.assign(`/login?from=${encodeURIComponent(from)}`)
  }
}

/** Synchronous cached user, falling back to a one-shot getSession. */
export async function resolveUser(): Promise<AuthUser | null> {
  const cached = getCachedUser()
  if (cached) return cached
  if (!isBrowser) return null
  try {
    const session = await authClient.getSession()
    const data = session && 'data' in session ? session.data : null
    if (!data?.user) return null
    const user = mapAuthUser(data.user as BetterAuthUser)
    setCachedUser(user)
    return user
  } catch {
    return null
  }
}
